import parse from 'html-react-parser'
import { FC, ReactElement, useCallback, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'

import { addItemToCart } from '../../../features/cart/cartSlice'
import { useI18n } from '../../../hooks'
import { useAppDispatchThenUnwrap, useAppSelector } from '../../../store'
import { CartItem, ProductWithVariants } from '../../../types'
import { getAmountWithCurrency } from '../../../utils'
import { PdpBreadCrumbsProp } from '../../breadcrumbs/pdpBreadCrumbs/pdpBreadCrumbs'
import Modal from '../../modal'
import RedirectionModal from '../../redirectionModal'
import { openSnackbar } from '../../snackbar'
import './index.css'
import './productDescriptionCard.css'

const ProductDescriptionCard: FC<{ product: ProductWithVariants }> = ({ product }): ReactElement => {
  const dispatchThenUnwrap = useAppDispatchThenUnwrap()
  const { settings } = useAppSelector((state) => state.appConfigReducer)
  const [isRedirectionModalOpen, setIsRedirectionModalOpen] = useState(false)
  const { locale } = settings
  const { asLowAs, name, description, variants: tmpVariants } = product
  const history = useHistory<PdpBreadCrumbsProp>()
  const toPath = `/products/${product.productId}`
  const startTime = 5
  const { t } = useI18n()

  const handleAddToCart = useCallback(
    async (item: CartItem): Promise<void> => {
      try {
        await dispatchThenUnwrap(addItemToCart(item))
        openSnackbar(`${t('Cart_yourCartWasUpdated')}`, 5, 'topLeft', 'success')
      } catch (reason) {
        openSnackbar(reason as string, 5, 'topLeft', 'failure')
      }
    },
    [dispatchThenUnwrap, t]
  )

  const variants = [...tmpVariants].sort((a, b) => a.pricing.price - b.pricing.price)

  const handleBuyNowOrAddToCart = useCallback((): JSX.Element => {
    const cartItem: CartItem = {
      variant: variants[0],
      quantity: 1,
      product,
      cartItemId: '',
      cartItemApiInfo: {
        cartItemId: '',
        productVariantId: variants[0].productVariantId as string,
        productVariantName: variants[0].description.short as string,
        productVariantType: variants[0].productVariantType,
        quantity: 1,
        priceInCart: variants[0].pricing.price,
        addOnForProductVariantId: null,
        isRequiredAddOn: false,
        isAccessGuaranteeNotApplicable: false,
        deliveryType: variants[0].deliveryType,
      },
    }
    const v = product.variants.find((v) => v.externalUrl)
    return v ? (
      <>
        <button
          style={{ cursor: 'pointer' }}
          className="product-description-card__links pdc-link-add-to-cart"
          onClick={() => setIsRedirectionModalOpen(true)}
          onKeyPress={(e) => e.key && e.key === 'Enter' && setIsRedirectionModalOpen(true)}
        >
          {parse(v.externalUrlLabel || `${t('Common_buyNow')}`)}
        </button>
        <Modal
          open={isRedirectionModalOpen}
          children={
            <RedirectionModal
              timerStartTime={startTime}
              thirdPartyWebsiteAddress={v.externalUrl || '/*'}
              thirdPartyWebsiteName={v.producer || 'third party website'}
              onRedirect={() => setIsRedirectionModalOpen(false)}
            />
          }
          onClose={() => setIsRedirectionModalOpen(false)}
          modalClassName={'redirection-modal'}
        ></Modal>
      </>
    ) : (
      <button className="product-description-card__links pdc-link-add-to-cart" onClick={async () => await handleAddToCart(cartItem)}>
        <span>{t('Common_addToCart')}</span>
      </button>
    )
  }, [handleAddToCart, isRedirectionModalOpen, product, t, variants])

  return (
    <>
      <Link
        to={{
          pathname: toPath,
          state: { path: history.location.pathname, search: history.location.search, productName: product.name, locationPath: toPath },
        }}
        title={name || 'Product Description Card'}
        style={{ textDecoration: 'none' }}
      >
        <div className="product-description-card__container">
          <div className="product-description-info">
            <p className="product-card__general__font--bold product-description-card-name white" style={{ textDecoration: 'none' }}>
              {name}
            </p>
            <p className="product-description-card-description white">{parse(description?.short || variants[0].description.short || '')}</p>
          </div>

          <div className="product-description-card__price__container">
            {asLowAs.price ? (
              <>
                <p className="product-description-card__price__startfrom white">{t('Common_startingFrom')}</p>
                <div
                  className="product-card__general__font--bold product-description-card__price__currentprice white"
                  aria-label={
                    t('Common_startingFromPrice') + ': ' + getAmountWithCurrency(locale.language, asLowAs.currencyType, asLowAs.price)
                  }
                >{`${getAmountWithCurrency(locale.language, asLowAs.currencyType, asLowAs.price)}`}</div>
                {asLowAs.msrp ? (
                  <>
                    <p
                      className="product-card__general__font--light product-description-card__price__oldprice"
                      role="note"
                      aria-label={t('Common_oldPrice') + ': ' + getAmountWithCurrency(locale.language, asLowAs.currencyType, asLowAs.msrp)}
                    >
                      {`${getAmountWithCurrency(locale.language, asLowAs.currencyType, asLowAs.msrp)}`}
                    </p>
                    <div className="product-description-card__price__saved">
                      <p
                        role="note"
                        aria-label={
                          t('Common_save') +
                          ' ' +
                          getAmountWithCurrency(locale.language, asLowAs.currencyType, asLowAs.msrp - asLowAs.price)
                        }
                      >
                        {`${t('Common_save')} ${getAmountWithCurrency(
                          locale.language,
                          asLowAs.currencyType,
                          asLowAs.msrp - asLowAs.price
                        )}`}
                      </p>
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              `${t('Common_free')}`
            )}
          </div>
        </div>
      </Link>

      {handleBuyNowOrAddToCart()}
    </>
  )
}

export default ProductDescriptionCard
